import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { TranslationFunctions } from '@i18n/i18n-types';
import { ColorSwatch, ColorBy, Pattern, SwatchBorderStyle, TableBorderStyle } from '@common/api';
import { Button } from '@components/button/Button';
import { NumberInputField, SwitchField } from '@components/form';
import {
    BorderDouble,
    BorderHatched,
    BorderThick,
    BorderThin,
    Close,
    PatternSolid,
    PatternHatched,
    Plus,
} from '@components/icons/FarseerIcons';
import { FlexGrow, FlexRow, FlexCol } from '@components/layout/Flex';
import { ToggleGroup } from '@components/toggle/ToggleGroup';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { Text } from '@components/typography/Typography';
import { Popover } from '@components/popover/Popover';
import { useToggle } from '@hooks/useToggle';
import { ColorSetKey, useColorContext } from '@providers/ColorProvider';
import { patch } from '@use-gpu/state';

import { appColors, predefinedDataColors } from '@base/useTheme';

import { ColorPickCustom } from './ColorPickCustom';
import { ColorPickSwatches } from './ColorPickSwatches';
import { ColorBadge } from './ColorBadge';
import { ColorBadgeSplit } from './ColorBadgeSplit';
import { ColorIconBadge } from './ColorIconBadge';

export type IconOption<T> = { value: T; icon: ReactElement; label: string };

export const getPatternOptions = (LL: TranslationFunctions): IconOption<Pattern>[] => [
    { value: Pattern.Solid, icon: <PatternSolid />, label: LL.SOLID() },
    { value: Pattern.Hatched, icon: <PatternHatched />, label: LL.HATCHED() },
    { value: Pattern.None, icon: <Close />, label: LL.NONE() },
];

export const getTableBorderOptions = (LL: TranslationFunctions): IconOption<TableBorderStyle>[] => [
    { value: TableBorderStyle.Thin, icon: <BorderThin />, label: LL.THIN() },
    { value: TableBorderStyle.Thick, icon: <BorderThick />, label: LL.THICK() },
    { value: TableBorderStyle.Double, icon: <BorderDouble />, label: LL.DOUBLE() },
    { value: TableBorderStyle.Hatched, icon: <BorderHatched />, label: LL.HATCHED() },
];

export const getSwatchBorderOptions = (LL: TranslationFunctions): IconOption<SwatchBorderStyle>[] => [
    { value: SwatchBorderStyle.Thin, icon: <BorderThin />, label: LL.THIN() },
    { value: SwatchBorderStyle.Thick, icon: <BorderThick />, label: LL.THICK() },
    { value: SwatchBorderStyle.None, icon: <Close />, label: LL.NONE() },
];

export const DEFAULT_SWATCH: Required<ColorSwatch> = {
    solid: 'rgba(0, 0, 0, 0)',
    positive: appColors.success.w500,
    negative: appColors.error.w500,
    neutral: appColors.ui.dark,
    border: appColors.ui.dark,

    borderStyle: SwatchBorderStyle.None,
    colorTotals: false,
    colorBy: ColorBy.Static,
    pattern: Pattern.Solid,

    threshold: 0,
};

export const HEIGHT_FULL = 309;

export type ColorPickerProps = {
    value?: string | null;

    disabled?: boolean;
    onChange?: (color: string) => void;

    round?: boolean;
};

export type ColorSwatchPickerProps = {
    value: ColorSwatch;

    disabled?: boolean;
    onChange?: (color: Required<ColorSwatch>) => void;

    round?: boolean;

    hasBorder?: boolean;
    hasPattern?: boolean;
    hasColorBy?: boolean;
};

export const ColorPicker = (props: ColorPickerProps) => {
    const { value, onChange, ...rest } = props;

    const swatch = useMemo(() => ({ solid: value ?? '#000' }), [value]);
    const handleChange = useCallback(
        (swatch: Required<ColorSwatch>) => {
            onChange?.(swatch.solid);
        },
        [onChange]
    );

    return <ColorSwatchPicker {...rest} value={swatch} onChange={handleChange} />;
};

export const ColorSwatchPicker = (props: ColorSwatchPickerProps) => {
    const { disabled, value, hasBorder, hasPattern, hasColorBy, onChange } = props;

    const { LL } = useI18nContext();
    const { customColors, addCustomColor } = useColorContext();

    const [isCustom, { on: customOn, off: customOff }] = useToggle(false);
    const [aspect, setAspect] = useState<ColorSetKey>('solid');

    const swatches = predefinedDataColors;
    const allSwatches = swatches.flat();

    const resolved = patch(DEFAULT_SWATCH, value) as Required<ColorSwatch>;
    const { solid, positive, negative, neutral, border, borderStyle, colorTotals, colorBy, pattern, threshold } =
        resolved;

    const hasSplitValence = colorBy !== ColorBy.Static;
    const hasFillColor = aspect !== 'solid' || pattern != Pattern.None;

    const firstColor = hasSplitValence ? positive : solid;
    const secondColor = hasSplitValence ? negative : solid;
    const colorBadge = (
        <ColorBadgeSplit
            disabled={disabled}
            first={firstColor}
            second={secondColor}
            firstBorder={!hasBorder || hasSplitValence ? firstColor : border}
            secondBorder={!hasBorder || hasSplitValence ? secondColor : border}
            borderStyle={borderStyle}
            pattern={pattern}
            round
        />
    );

    const handleCommitCustom = (color?: string) => {
        if (color && !allSwatches.includes(color)) addCustomColor(aspect, color);
        customOff();
    };

    const handleDismissPopup = () => {
        if (value[aspect] != null && isCustom) handleCommitCustom(value[aspect]);
        setAspect('solid');
    };

    const handleChangeCurrentColor = (color: string) => handleChangeColor(aspect, color);
    const handleChangeColor = (aspect: ColorSetKey, color: string) =>
        onChange?.({
            ...resolved,
            [aspect]: color,
        });

    const handleChangeBorderStyle = (borderStyle: SwatchBorderStyle) =>
        onChange?.({
            ...resolved,
            borderStyle,
        });
    const handleChangeColorBy = (colorBy: ColorBy) =>
        onChange?.({
            ...resolved,
            colorBy,
        });
    const handleChangeColorTotals = (colorTotals: boolean) =>
        onChange?.({
            ...resolved,
            colorTotals,
        });
    const handleChangePattern = (pattern: Pattern) =>
        onChange?.({
            ...resolved,
            pattern,
        });
    const handleChangeThreshold = (threshold: number) =>
        onChange?.({
            ...resolved,
            threshold,
        });

    const resolvedColor = resolved[aspect];

    if (disabled) return colorBadge;

    const colorByNode =
        hasColorBy && aspect === 'solid' ? (
            <ToggleGroup $size="small" value={colorBy} onChange={handleChangeColorBy}>
                <FlexRow $justify="center" $width="100%" $flexGrow={1}>
                    <ToggleGroup.Toggle title={LL.STATIC()} value={ColorBy.Static} style={{ flexGrow: 1 }} />
                    <ToggleGroup.Toggle title={LL.BY_VALUE()} value={ColorBy.Value} style={{ flexGrow: 1 }} />
                    <ToggleGroup.Toggle title={LL.BY_TREND()} value={ColorBy.Trend} style={{ flexGrow: 1 }} />
                </FlexRow>
            </ToggleGroup>
        ) : null;

    const aspectNames = {
        solid: LL.COLOR(),
        positive: LL.CONDITIONAL_FORMATTING_POLARITY_OPTIONS.Positive(),
        negative: LL.CONDITIONAL_FORMATTING_POLARITY_OPTIONS.Negative(),
        neutral: LL.CONDITIONAL_FORMATTING_POLARITY_OPTIONS.Neutral(),
        border: LL.BORDER(),
    };

    const splitValenceNode =
        hasSplitValence && aspect === 'solid' ? (
            <FlexCol $gap={8}>
                <FlexRow>
                    <FlexCol $flexGrow={1}>
                        <Text $muted>{aspectNames.positive}</Text>
                        <FlexRow>
                            <ColorBadge color={positive} round onClick={() => setAspect('positive')} />
                        </FlexRow>
                    </FlexCol>
                    <FlexCol $flexGrow={1}>
                        <Text $muted>{aspectNames.neutral}</Text>
                        <FlexRow>
                            <ColorBadge color={neutral} round onClick={() => setAspect('neutral')} />
                        </FlexRow>
                    </FlexCol>
                    <FlexCol $flexGrow={1}>
                        <Text $muted>{aspectNames.negative}</Text>
                        <FlexRow>
                            <ColorBadge color={negative} round onClick={() => setAspect('negative')} />
                        </FlexRow>
                    </FlexCol>
                </FlexRow>

                {colorBy === ColorBy.Value ? (
                    <NumberInputField
                        size="small"
                        label={LL.THRESHOLD_VALUE()}
                        value={threshold}
                        onChange={handleChangeThreshold}
                    />
                ) : null}

                {colorBy === ColorBy.Trend ? (
                    <SwitchField
                        size="small"
                        label={LL.COLOR_TOTALS_BY_VALUE()}
                        value={colorTotals}
                        onChange={handleChangeColorTotals}
                    />
                ) : null}
            </FlexCol>
        ) : null;

    const paletteNode = (
        <FlexCol $gap={4} style={{ opacity: hasFillColor ? 1 : 0.5 }}>
            <FlexCol>
                {swatches.map((row, i) => (
                    <FlexRow key={i.toString()}>
                        <ColorPickSwatches
                            disabled={!hasFillColor}
                            value={resolvedColor}
                            colors={row}
                            onChange={handleChangeCurrentColor}
                        />
                    </FlexRow>
                ))}
            </FlexCol>
            <FlexCol>
                <Text $muted>{LL.CUSTOM()}</Text>
                <FlexRow>
                    <FlexRow $gap={2}>
                        <FlexRow>
                            <ColorPickSwatches
                                disabled={!hasFillColor}
                                value={resolvedColor}
                                colors={customColors[aspect]}
                                onChange={handleChangeCurrentColor}
                            />
                        </FlexRow>
                        <Button
                            disabled={!hasFillColor}
                            type="button"
                            $size="small"
                            $type="quaternary"
                            icon={Plus}
                            alignIcon="left"
                            onClick={customOn}
                        />
                    </FlexRow>
                </FlexRow>
            </FlexCol>
        </FlexCol>
    );

    const patternNode =
        hasPattern && aspect === 'solid' ? (
            <div>
                <Text $muted>{LL.FILL_STYLE()}</Text>
                <FlexRow>
                    {getPatternOptions(LL).map((option) => (
                        <TooltipWrapper
                            key={option.value}
                            label={option.label}
                            allowedPlacements={['bottom']}
                            offsetY={2}
                        >
                            <ColorIconBadge
                                disabled={disabled}
                                icon={option.icon}
                                selected={pattern === option.value}
                                round
                                onClick={() => handleChangePattern(option.value)}
                            />
                        </TooltipWrapper>
                    ))}
                </FlexRow>
            </div>
        ) : null;

    const borderNode =
        hasBorder && aspect === 'solid' ? (
            <div>
                <Text $muted>{LL.BORDER()}</Text>
                <FlexRow>
                    {getSwatchBorderOptions(LL).map((option) => (
                        <TooltipWrapper
                            key={option.value}
                            label={option.label}
                            allowedPlacements={['bottom']}
                            offsetY={2}
                        >
                            <ColorIconBadge
                                disabled={disabled}
                                icon={option.icon}
                                selected={borderStyle === option.value}
                                round
                                onClick={() => handleChangeBorderStyle(option.value)}
                            />
                        </TooltipWrapper>
                    ))}
                    {!hasSplitValence && borderStyle !== SwatchBorderStyle.None ? (
                        <>
                            <FlexGrow />
                            <ColorBadge color={border} round onClick={() => setAspect('border')} />
                        </>
                    ) : null}
                </FlexRow>
            </div>
        ) : null;

    const doneNode =
        aspect !== 'solid' ? (
            <Button
                title={LL.DONE()}
                type="button"
                $size="small"
                $type="tertiary"
                onClick={() => {
                    setAspect('solid');
                }}
            />
        ) : null;

    const height = hasColorBy && !isCustom && aspect === 'solid' ? HEIGHT_FULL : 'auto';

    return (
        <Popover onRequestClose={handleDismissPopup}>
            <Popover.Trigger>
                <div>{colorBadge}</div>
            </Popover.Trigger>
            <Popover.Content $padding="7px" $zIndex={1200}>
                <FlexCol
                    $gap={8}
                    // Extra padding for selection outline spill
                    style={{ padding: 1, height }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {isCustom ? (
                        <>
                            <ColorPickCustom
                                value={resolvedColor}
                                onChange={handleChangeCurrentColor}
                                onCancel={customOff}
                                onCommit={() => handleCommitCustom(value[aspect])}
                            />
                        </>
                    ) : (
                        <>
                            <div style={{ width: 216, height }}>
                                <Text $muted>{aspectNames[aspect]}</Text>
                                <FlexCol $gap={splitValenceNode ? 8 : 2}>
                                    <div>{colorByNode}</div>
                                    {splitValenceNode ?? paletteNode}
                                </FlexCol>
                            </div>
                            <FlexRow>
                                <FlexCol $width="50%">{patternNode}</FlexCol>
                                <FlexCol $width="50%">{borderNode}</FlexCol>
                            </FlexRow>
                            {doneNode}
                        </>
                    )}
                </FlexCol>
            </Popover.Content>
        </Popover>
    );
};
